import { axiosInstance } from '../App';

/**
 * Upload an image to Cloudinary via signed upload
 * @param {File|Blob|string} file - File object, Blob, or base64 string
 * @param {string} folder - Cloudinary folder (orders, approvals, kitty, etc.)
 * @returns {Promise<{url: string, public_id: string}>}
 */
export async function uploadToCloudinary(file, folder = 'uploads') {
  try {
    // Get signature from backend
    const signatureRes = await axiosInstance.get(`/cloudinary/signature?folder=${folder}&resource_type=image`);
    const sig = signatureRes.data;

    // Create form data
    const formData = new FormData();
    
    // Handle different input types
    if (typeof file === 'string' && file.startsWith('data:')) {
      // Convert base64 to blob
      const response = await fetch(file);
      const blob = await response.blob();
      formData.append('file', blob);
    } else if (file instanceof Blob || file instanceof File) {
      formData.append('file', file);
    } else {
      throw new Error('Invalid file type');
    }

    formData.append('api_key', sig.api_key);
    formData.append('timestamp', sig.timestamp);
    formData.append('signature', sig.signature);
    formData.append('folder', sig.folder);

    // Upload to Cloudinary
    const uploadRes = await fetch(
      `https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`,
      { method: 'POST', body: formData }
    );

    if (!uploadRes.ok) {
      const error = await uploadRes.json();
      throw new Error(error.error?.message || 'Upload failed');
    }

    const result = await uploadRes.json();
    return {
      url: result.secure_url,
      public_id: result.public_id
    };
  } catch (error) {
    console.error('Cloudinary upload error:', error);
    throw error;
  }
}

/**
 * Upload multiple images to Cloudinary with batched signature request
 * @param {Array<File|Blob|string>} files - Array of files or base64 strings
 * @param {string} folder - Cloudinary folder
 * @param {function} onProgress - Optional progress callback (current, total)
 * @returns {Promise<Array<{url: string, public_id: string}>>}
 */
export async function uploadMultipleToCloudinary(files, folder = 'uploads', onProgress) {
  if (!files || files.length === 0) return [];
  
  try {
    // Get signature once for all uploads (valid for ~1 hour)
    const signatureRes = await axiosInstance.get(`/cloudinary/signature?folder=${folder}&resource_type=image`);
    const sig = signatureRes.data;
    
    // Upload all files in parallel with shared signature
    const uploadPromises = files.map(async (file, index) => {
      const formData = new FormData();
      
      // Handle different input types
      if (typeof file === 'string' && file.startsWith('data:')) {
        const response = await fetch(file);
        const blob = await response.blob();
        formData.append('file', blob);
      } else if (file instanceof Blob || file instanceof File) {
        formData.append('file', file);
      } else {
        throw new Error('Invalid file type');
      }

      formData.append('api_key', sig.api_key);
      formData.append('timestamp', sig.timestamp);
      formData.append('signature', sig.signature);
      formData.append('folder', sig.folder);

      const uploadRes = await fetch(
        `https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`,
        { method: 'POST', body: formData }
      );

      if (!uploadRes.ok) {
        const error = await uploadRes.json();
        throw new Error(error.error?.message || 'Upload failed');
      }

      const result = await uploadRes.json();
      
      if (onProgress) {
        onProgress(index + 1, files.length);
      }
      
      return {
        url: result.secure_url,
        public_id: result.public_id
      };
    });
    
    return await Promise.all(uploadPromises);
  } catch (error) {
    console.error('Cloudinary batch upload error:', error);
    throw error;
  }
}

/**
 * Delete an image from Cloudinary
 * @param {string} publicId - The public_id of the image to delete
 * @returns {Promise<void>}
 */
export async function deleteFromCloudinary(publicId) {
  try {
    await axiosInstance.delete(`/cloudinary/delete?public_id=${encodeURIComponent(publicId)}`);
  } catch (error) {
    console.error('Cloudinary delete error:', error);
    throw error;
  }
}

/**
 * Check if a URL is a Cloudinary URL
 * @param {string} url 
 * @returns {boolean}
 */
export function isCloudinaryUrl(url) {
  return url && typeof url === 'string' && url.includes('cloudinary.com');
}

/**
 * Extract public_id from a Cloudinary URL
 * @param {string} url 
 * @returns {string|null}
 */
export function extractPublicId(url) {
  if (!isCloudinaryUrl(url)) return null;
  
  try {
    const parts = url.split('/upload/');
    if (parts.length > 1) {
      let path = parts[1];
      // Remove version if present (v1234567890/)
      if (path.startsWith('v')) {
        path = path.split('/').slice(1).join('/');
      }
      // Remove extension
      return path.replace(/\.[^/.]+$/, '');
    }
  } catch (e) {
    console.error('Error extracting public_id:', e);
  }
  return null;
}

/**
 * Check if a string is a base64 data URL
 * @param {string} str 
 * @returns {boolean}
 */
export function isBase64DataUrl(str) {
  return str && typeof str === 'string' && str.startsWith('data:');
}

/**
 * Convert all base64 images in an array to Cloudinary URLs
 * Keeps existing Cloudinary URLs as-is
 * @param {Array<string>} images - Array of image URLs or base64 strings
 * @param {string} folder - Cloudinary folder
 * @param {function} onProgress - Optional progress callback
 * @returns {Promise<Array<string>>} Array of Cloudinary URLs
 */
export async function migrateImagesToCloudinary(images, folder = 'uploads', onProgress) {
  if (!images || images.length === 0) return [];
  
  const results = [];
  for (let i = 0; i < images.length; i++) {
    const img = images[i];
    
    if (isCloudinaryUrl(img)) {
      // Already on Cloudinary, keep as-is
      results.push(img);
    } else if (isBase64DataUrl(img)) {
      // Upload to Cloudinary
      const uploaded = await uploadToCloudinary(img, folder);
      results.push(uploaded.url);
    } else {
      // Unknown format, keep as-is
      results.push(img);
    }
    
    if (onProgress) {
      onProgress(i + 1, images.length);
    }
  }
  
  return results;
}
